import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShareModal } from '@/components/ShareModal';
import type { Scrapbook } from '@shared/types';

interface EditorTopBarProps {
  scrapbook: Scrapbook;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  onSave: () => Promise<void>;
}

export function EditorTopBar({
  scrapbook,
  isSaving,
  hasUnsavedChanges,
  onSave,
}: EditorTopBarProps) {
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handlePreview = () => {
    // Open preview in new tab (would need share link)
    window.open(`/view/${scrapbook.id}`, '_blank');
  };

  const getSaveStatus = () => {
    if (isSaving) {
      return { text: 'Saving...', indicator: 'saving' };
    }
    if (hasUnsavedChanges) {
      return { text: 'Unsaved changes', indicator: 'unsaved' };
    }
    return { text: 'Saved', indicator: 'saved' };
  };

  const status = getSaveStatus();

  return (
    <>
      <header className="editor-topbar">
        <div className="editor-topbar-left">
          <button
            className="editor-topbar-back"
            onClick={handleBack}
            aria-label="Back to dashboard"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="editor-topbar-title">{scrapbook.title}</h1>
        </div>

        <div className="editor-topbar-center">
          <div className="editor-topbar-status">
            <span className={`editor-topbar-status-indicator ${status.indicator}`} />
            <span>{status.text}</span>
          </div>
        </div>

        <div className="editor-topbar-right">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onSave()}
            disabled={isSaving || !hasUnsavedChanges}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setIsShareModalOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handlePreview}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Preview
          </button>
        </div>
      </header>

      <ShareModal
        scrapbookId={scrapbook.id}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </>
  );
}
